import { Badge, Typography } from '@lucid/lego';
import type { TestDriveBookingRecord } from '../../../../api/services/lupp/test-drive/dto.test-drive';
import { formatDateTime, formatDayMonthYearTime } from '../../../../utils/tools';
import {
  formatTestDriveId,
  getBookingStatusBadgeColor,
  getBookingStatusLabel,
  getProspectDisplayName,
  getTestDriveTypeLabel,
} from '../utils';
import styles from './utils.module.scss';

/** Locale-aware default for the "Prospect Name" column header, used while Contentful labels are unset. */
const DEFAULT_PROSPECT_NAME_LABEL: Record<string, string> = {
  'de-de': 'Name des Interessenten',
};

const getDefaultProspectNameLabel = (locale: string) =>
  DEFAULT_PROSPECT_NAME_LABEL[locale.toLowerCase()] || 'Prospect Name';

type CreateColumnsParams = {
  theme: 'light' | 'dark';
  labels: Record<string, string>;
  locale?: string;
  /** Opens the Prospect slider on the Test Drive tab, deep-linked to this specific booking. */
  onTestDriveClick?: (prospectIdentifier: string, testDriveScheduleId: string) => void;
  /** Opens the Prospect slider on its default (Detail) view. */
  onProspectClick?: (prospectIdentifier: string) => void;
};

export const createColumns = ({
  theme,
  labels,
  locale = 'en-US',
  onTestDriveClick,
  onProspectClick,
}: CreateColumnsParams) => [
  {
    key: 'testDriveId',
    title: labels.test_drive_id || 'Test Drive ID',
    minWidth: 140,
    sortable: true,
    render: (_: unknown, record: TestDriveBookingRecord) =>
      record.testDriveScheduleId && record.prospectIdentifier ? (
        <Typography
          variant="body"
          size="small"
          color="link"
          theme={theme}
          title={record.testDriveScheduleId}
          onClick={() =>
            onTestDriveClick?.(record.prospectIdentifier as string, record.testDriveScheduleId)
          }
          className={styles.clickable}
        >
          {formatTestDriveId(record.testDriveScheduleId)}
        </Typography>
      ) : (
        <Typography variant="body" size="small" color="black" theme={theme}>
          {formatTestDriveId(record.testDriveScheduleId)}
        </Typography>
      ),
  },
  {
    key: 'prospectName',
    title: labels.prospect_name || getDefaultProspectNameLabel(locale),
    minWidth: 180,
    sortable: true,
    render: (_: unknown, record: TestDriveBookingRecord) => {
      const label = getProspectDisplayName(record) || record.prospectIdentifier;
      return record.prospectIdentifier && label ? (
        <Typography
          variant="body"
          size="small"
          color="link"
          theme={theme}
          onClick={() => onProspectClick?.(record.prospectIdentifier as string)}
          className={styles.clickable}
        >
          {label}
        </Typography>
      ) : (
        <Typography variant="body" size="small" color="black" theme={theme}>
          -
        </Typography>
      );
    },
  },
  {
    key: 'model',
    title: labels.model || 'Model',
    minWidth: 140,
    sortable: true,
    render: (_: unknown, record: TestDriveBookingRecord) => (
      <Typography variant="body" size="small" color="black" theme={theme}>
        {[record.model, record.trim].filter(Boolean).join(' ') || '-'}
      </Typography>
    ),
  },
  {
    key: 'vin',
    title: labels.vin || 'VIN',
    minWidth: 180,
    sortable: true,
    render: (_: unknown, record: TestDriveBookingRecord) => (
      <Typography variant="body" size="small" color="black" theme={theme}>
        {record.vin || '-'}
      </Typography>
    ),
  },
  {
    key: 'testDriveType',
    title: labels.test_drive_type || 'Test Drive Type',
    minWidth: 150,
    sortable: true,
    render: (_: unknown, record: TestDriveBookingRecord) => (
      <Typography variant="body" size="small" color="black" theme={theme}>
        {getTestDriveTypeLabel(record.testDriveType, labels)}
      </Typography>
    ),
  },
  {
    key: 'appointmentStart',
    title: labels.appointment_start_datetime || 'Appointment Start Date & Time',
    minWidth: 180,
    sortable: true,
    render: (_: unknown, record: TestDriveBookingRecord) => (
      <Typography variant="body" size="small" color="black" theme={theme}>
        {formatDayMonthYearTime(record.scheduledStartDatetime, locale, record.timezone)}
      </Typography>
    ),
  },
  {
    key: 'appointmentEnd',
    title: labels.appointment_end_datetime || 'Appointment End Date & Time',
    minWidth: 180,
    sortable: true,
    render: (_: unknown, record: TestDriveBookingRecord) => (
      <Typography variant="body" size="small" color="black" theme={theme}>
        {formatDayMonthYearTime(record.scheduledEndDatetime, locale, record.timezone)}
      </Typography>
    ),
  },
  {
    key: 'status',
    title: labels.status || 'Status',
    minWidth: 130,
    sortable: true,
    render: (_: unknown, record: TestDriveBookingRecord) => {
      const status = record.effectiveStatus || record.testDriveStatus;
      return (
        <Badge
          label={getBookingStatusLabel(status, labels)}
          color={getBookingStatusBadgeColor(status)}
          type="secondary"
          theme={theme === 'dark' ? 'light' : 'dark'}
          minWidth="60px"
        />
      );
    },
  },
  {
    key: 'host',
    title: labels.host || 'Host',
    minWidth: 160,
    sortable: true,
    render: (_: unknown, record: TestDriveBookingRecord) => (
      <Typography variant="body" size="small" color="black" theme={theme}>
        {record.hostName || '-'}
      </Typography>
    ),
  },
  {
    key: 'location',
    title: labels.location || 'Location',
    minWidth: 160,
    sortable: true,
    render: (_: unknown, record: TestDriveBookingRecord) => (
      <Typography variant="body" size="small" color="black" theme={theme}>
        {record.appointmentLocationName || '-'}
      </Typography>
    ),
  },
  {
    key: 'businessPartnerUserId',
    title: labels.booked_by || 'Booked By',
    minWidth: 160,
    sortable: false,
    hideable: true,
    render: (_: unknown, record: TestDriveBookingRecord) => (
      <Typography variant="body" size="small" color="black" theme={theme}>
        {record.bookedByUser?.name || record.businessPartnerUserId || '-'}
      </Typography>
    ),
  },
  {
    key: 'createdDatetime',
    title: labels.created || 'Created',
    minWidth: 160,
    sortable: true,
    hideable: true,
    render: (_: unknown, record: TestDriveBookingRecord) => (
      <Typography variant="body" size="small" color="black" theme={theme}>
        {formatDateTime(record.createdDatetime, locale)}
      </Typography>
    ),
  },
];
